import type { LucideIcon } from 'lucide-react';
import NamedIcon from './ui/NamedIcon';
import styles from './PageHeader.module.css';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Single Lucide icon — historical API. Use this when no Lottie
   *  slot is reserved for the surface. */
  icon?: LucideIcon;
  /** Lottie slot filename stem (no `.json`). When set, the header
   *  renders via NamedIcon; the slot file at /icons/{iconSlot}.json
   *  plays, or `iconFallback` Lucide renders when the file is
   *  absent. Mutually exclusive with `icon` — `iconSlot` wins when
   *  both are provided. */
  iconSlot?: string;
  /** Lucide fallback for the slot path. Required alongside
   *  `iconSlot` so missing slot files have something to render. */
  iconFallback?: LucideIcon;
  actions?: React.ReactNode;
}

export function PageHeader({
  title, subtitle, icon: Icon, iconSlot, iconFallback: Fallback, actions,
}: PageHeaderProps) {
  return (
    <header className={styles.header}>
      <div className={styles.left}>
        {iconSlot && Fallback ? (
          <div className={styles.icon}>
            <NamedIcon
              name={iconSlot}
              fallback={<Fallback size={20} />}
              size={20}
            />
          </div>
        ) : Icon ? (
          <div className={styles.icon}>
            <Icon size={20} />
          </div>
        ) : null}
        <div>
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </header>
  );
}
